import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Divider } from 'antd';
import { MacCommandOutlined } from '@ant-design/icons';
import { Catcher, LoadingApiByIds } from '../';
import cx from 'classnames';
import { ITariffPackagesToBuyListItem, ITariffPackagesToBuyListPaginated } from '../../interfaces';
import { useProfile, useRouterStore, useUiIsSpinnerFound } from '../../hooks';
import {
    getCompaniesChildrenSelect,
    getCountriesSelect,
    getOperatorsSelect,
    getTariffPackagesToBuyList,
} from '../../middleware';
import { tB, tNM, tT } from '../../translate';
import { InfiniteScroll } from './Components/InfiniteScroll';
import { TariffItemPackageViewCard } from './Components/TariffItem/TariffItemPackageView';
import CardStyles from '../styles/m_itemCardStyles.less';
import TariffsStyles from './m_styles.less';

export const TariffPackagesToBuyList: React.FC = observer(() => {
    const companyId = useProfile().companyId;
    const [page, setPage] = useState(1);
    const [packageList, setPackageList] = useState<ITariffPackagesToBuyListItem[]>([]);
    const routerStore = useRouterStore();
    const isLoading = useUiIsSpinnerFound(['GET_TARIFFS_PACKAGES_TO_BUY']);
    const [totalPackage, setTotalPackage] = useState(0);

    const pageSize = 10;

    const goTo = (event: React.MouseEvent<HTMLDivElement>, id: string | undefined): void => {
        if (!(event.ctrlKey || event.metaKey || event.altKey || event.shiftKey)) {
            event.preventDefault();

            routerStore.goTo('TariffPackagesToBuyItem', { params: { id } }).then();
        }
    };

    const fetchData = async (page: number, reset?: boolean) => {
        const resp = (await getTariffPackagesToBuyList(
            { pageNumber: page, pageSize: pageSize },
            true,
        )) as ITariffPackagesToBuyListPaginated;

        setTotalPackage(resp.meta.records_count || 0);

        if (reset) {
            setPackageList(resp.data);
        } else {
            setPackageList((prevList) => [...prevList, ...resp.data]);
        }
    };

    useEffect(() => {
        setPackageList([]);
        setPage(1);
        fetchData(1);
    }, []);

    useEffect(() => {
        getOperatorsSelect().then();
        getCountriesSelect().then();

        if (companyId) {
            getCompaniesChildrenSelect(companyId || '', true).then();
        }
    }, []);

    return (
        <Catcher>
            <div className={cx(CardStyles.main, TariffsStyles.container)}>
                <div className={TariffsStyles.tariffItemContainer}>
                    <div className={TariffsStyles.tariffItemHeaderContainer}>
                        <div>
                            <div className={TariffsStyles.header}>
                                <MacCommandOutlined className={TariffsStyles.icon} />
                                {tB('Packages To Buy')}
                            </div>
                            <p>{tT('Buy packages for your company')}</p>
                        </div>
                    </div>
                    <Divider />
                    <InfiniteScroll
                        pageSize={pageSize}
                        page={page}
                        fetchFunc={fetchData}
                        totalPackage={totalPackage}
                        currentPackages={packageList.length}
                        handleSetPage={setPage}
                    >
                        <div className={TariffsStyles.infiniteScrollContainer}>
                            <div className={cx(TariffsStyles.grid, TariffsStyles.packageToByGrid)}>
                                {packageList.map((tariffPackages) => (
                                    <div key={tariffPackages.id} onClick={(e) => goTo(e, tariffPackages.id.toString())}>
                                        <TariffItemPackageViewCard
                                            packageInfo={tariffPackages}
                                            key={tariffPackages.id}
                                            isClickable={true}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </InfiniteScroll>
                    {packageList && packageList.length === 0 && !isLoading && (
                        <div className={TariffsStyles.notFound}>{tNM('No packages found')}</div>
                    )}
                </div>
                <LoadingApiByIds idSpinners={['GET_TARIFFS_PACKAGES_TO_BUY']} />
            </div>
        </Catcher>
    );
});
